import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sortByDisplayLabel(items, key = "name") {
  return [...items].sort((a, b) => String(a?.[key] || "").localeCompare(String(b?.[key] || "")));
}

function TextField({ label, value, onChange, required, multiline = false }) {
  return (
    <label className="block space-y-1 text-xs text-amber-200/90">
      <span className="font-medium text-amber-100">
        {label}
        {required && <span className="text-red-400">*</span>}
      </span>
      {multiline ? (
        <textarea
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs resize-vertical"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
        />
      )}
    </label>
  );
}

function QuestionRenderer({ questions, answers, setAnswers }) {
  const handleChange = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  if (!questions || questions.length === 0) return null;

  return (
    <div className="space-y-3">
      {questions.map((q) => {
        const value = answers[q.id] ?? (q.type === "checkbox" ? false : "");
        if (q.type === "text") {
          return (
            <TextField
              key={q.id}
              label={q.label}
              value={value}
              onChange={(v) => handleChange(q.id, v)}
              required={q.required}
              multiline
            />
          );
        }
        if (q.type === "dropdown") {
          return (
            <label
              key={q.id}
              className="block space-y-1 text-xs text-amber-200/90"
            >
              <span className="font-medium text-amber-100">
                {q.label}
                {q.required && <span className="text-red-400">*</span>}
              </span>
              <select
                value={value || ""}
                onChange={(e) => handleChange(q.id, e.target.value)}
                className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
              >
                <option value="">Select…</option>
                {(q.options || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>
          );
        }
        if (q.type === "multi-select") {
          const current = Array.isArray(value) ? value : [];
          const toggle = (opt) => {
            const set = new Set(current);
            if (set.has(opt)) set.delete(opt);
            else set.add(opt);
            handleChange(q.id, Array.from(set));
          };
          return (
            <div
              key={q.id}
              className="space-y-1 text-xs text-amber-200/90"
            >
              <span className="font-medium text-amber-100">
                {q.label}
                {q.required && <span className="text-red-400">*</span>}
              </span>
              <div className="flex flex-wrap gap-2">
                {(q.options || []).map((opt) => {
                  const checked = current.includes(opt);
                  return (
                    <button
                      type="button"
                      key={opt}
                      onClick={() => toggle(opt)}
                      className={`px-2 py-0.5 rounded-full border text-[11px] ${
                        checked
                          ? "bg-amber-600 text-black border-amber-400"
                          : "bg-black/40 text-amber-100 border-amber-700/60 hover:bg-amber-900/40"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        }
        if (q.type === "checkbox") {
          return (
            <label
              key={q.id}
              className="inline-flex items-center gap-2 text-xs text-amber-200/90"
            >
              <input
                type="checkbox"
                checked={!!value}
                onChange={(e) => handleChange(q.id, e.target.checked)}
                className="h-3 w-3"
              />
              <span>
                {q.label}
                {q.required && <span className="text-red-400">*</span>}
              </span>
            </label>
          );
        }
        return null;
      })}
    </div>
  );
}

export default function HowlingForms() {
  const { category } = useParams();

  const [settings, setSettings] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Common identity fields
  const [name, setName] = useState("");
  const [slUsername, setSlUsername] = useState("");
  const [discordUsername, setDiscordUsername] = useState("");

  // Report-specific
  const [reportedStaffId, setReportedStaffId] = useState("");

  // Feedback-specific
  const [feedbackType, setFeedbackType] = useState("");
  const [anonymous, setAnonymous] = useState(false);

  // Applications
  const [appType, setAppType] = useState("");
  const [staffAck, setStaffAck] = useState(false);
  const [leadOrg, setLeadOrg] = useState("");
  const [appDepartments, setAppDepartments] = useState([]); // selected department ids
  const [staffPositions, setStaffPositions] = useState({}); // { [deptId]: string[] positionIds }

  // Dynamic question answers
  const [answers, setAnswers] = useState({});

  const isReport = category === "report-staff";
  const isFeedback = category === "feedback";
  const isApplications = category === "applications";

  const title = useMemo(() => {
    if (isReport) return "Report a Staff Member";
    if (isFeedback) return "Feedback";
    if (isApplications) return "Staff / Lead Applications";
    return "Forms";
  }, [isReport, isFeedback, isApplications]);

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [settingsRes, staffRes] = await Promise.all([
        axios.get(`${API}/howling_hills/admin/settings`),
        isReport ? axios.get(`${API}/howling_hills/staff`) : Promise.resolve({ data: [] }),
      ]);
      setSettings(settingsRes.data);
      setStaff(staffRes.data || []);
    } catch (e) {
      console.error(e);
      setError("Failed to load form configuration. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [category]);

  // Keep staff position selections in sync with selected departments
  useEffect(() => {
    setStaffPositions((prev) => {
      const next = {};
      appDepartments.forEach((id) => {
        if (prev[id] && prev[id].length) {
          next[id] = prev[id];
        }
      });
      return next;
    });
  }, [appDepartments]);


  const resetForm = () => {
    setName("");
    setSlUsername("");
    setDiscordUsername("");
    setReportedStaffId("");
    setFeedbackType("");
    setAnonymous(false);
    setAppType("");
    setStaffAck(false);
    setLeadOrg("");
    setAppDepartments([]);
    setStaffPositions({});
    setAnswers({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!settings) return;
    setError("");
    setSuccess("");

    // Basic client-side required checks
    if (isReport) {
      if (!reportedStaffId) {
        setError("Please select the staff member you are reporting.");
        return;
      }
      if (!slUsername) {
        setError("Please provide your Second Life username.");
        return;
      }
    }

    if (isFeedback) {
      if (!feedbackType) {
        setError("Please select a feedback type.");
        return;
      }
      if (!anonymous && (!name || !slUsername)) {
        setError("Please provide your name and Second Life username or choose anonymous.");
        return;
      }
    }

    if (isApplications) {
      if (!appType) {
        setError("Please select an application type.");
        return;
      }
      if (!name || !slUsername) {
        setError("Please provide your name and Second Life username.");
        return;
      }
      if (appType === "Staff Application" && !staffAck) {
        setError("Please acknowledge the staff disclaimer before submitting.");
        return;
      }
      if (appType === "Lead Application" && !leadOrg) {
        setError("Please select an organization before submitting.");
        return;
      }
    }

    const reported = staff.find((s) => s.id === reportedStaffId);

    const payload = {
      category: isReport ? "report" : isFeedback ? "feedback" : "application",
      sub_type: isFeedback ? feedbackType : isApplications ? appType : null,
      anonymous: isFeedback ? anonymous : false,
      name: anonymous || isReport ? null : name || null,
      sl_username: anonymous ? null : slUsername || null,
      discord_username: anonymous ? null : discordUsername || null,
      reported_staff_id: isReport ? reportedStaffId || null : null,
      reported_staff_name: isReport ? reported?.username || null : null,
      answers: {
        ...answers,
        ...(isApplications
          ? {
              application_type: appType,
              staff_departments: appDepartments,
              staff_positions: Object.keys(staffPositions).reduce((acc, deptId) => {
                const positions = staffPositions[deptId] || [];
                if (positions.length) {
                  acc.push({ department_id: deptId, position_ids: positions });
                }
                return acc;
              }, []),
              lead_organization: leadOrg || null,
            }
          : {}),
      },
    };

    setSubmitting(true);
    try {
      await axios.post(`${API}/howling_hills/forms/submit`, payload);
      setSuccess("Your submission has been sent. Thank you!");
      resetForm();
    } catch (e) {
      console.error(e);
      const backendMessage = e?.response?.data?.detail;
      if (backendMessage) {
        setError(typeof backendMessage === "string" ? backendMessage : String(backendMessage));
      } else {
        setError("Failed to submit form. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const staffDepartments = useMemo(() => sortByDisplayLabel(settings?.staff_departments || []), [settings?.staff_departments]);
  const adminDepartments = useMemo(() => sortByDisplayLabel(settings?.admin_departments || []), [settings?.admin_departments]);
  const organizationPositions = useMemo(
    () => sortByDisplayLabel(settings?.organization_positions || []),
    [settings?.organization_positions]
  );

  const currentQuestions = useMemo(() => {
    if (!settings) return [];
    if (isReport) return sortByDisplayLabel(settings.report_questions || [], "label");
    if (isFeedback) {
      if (feedbackType === "Backer Feedback") return sortByDisplayLabel(settings.feedback_backer_questions || [], "label");
      return sortByDisplayLabel(settings.feedback_questions || [], "label");
    }
    if (!isApplications) return [];
    if (appType === "Lead Application") return sortByDisplayLabel(settings.lead_questions || [], "label");
    if (appType === "Admin Application") return sortByDisplayLabel(settings.admin_questions || [], "label");
    if (appType === "Item Creation") return sortByDisplayLabel(settings.item_questions || [], "label");
    if (appType === "Sim VIP/Backers Waitlist") return sortByDisplayLabel(settings.sim_vip_waitlist_questions || [], "label");
    // Default to staff questions for Staff Application
    return sortByDisplayLabel(settings.staff_questions || [], "label");
  }, [settings, isReport, isFeedback, isApplications, appType, feedbackType]);

  return (
    <section className="space-y-6 max-w-4xl lg:max-w-5xl">
      <header>
        <h2 className="text-2xl font-semibold text-amber-100 mb-1">{title}</h2>
        {isReport && (
          <p className="text-sm text-amber-200/80">
            Use this form to confidentially report concerns regarding a staff member. Reports are routed to the
            appropriate admin leadership.
          </p>
        )}
        {isFeedback && (
          <p className="text-sm text-amber-200/80">
            Share feedback about HUD, builds, general experience, or backer matters. Anonymous submissions are
            allowed.
          </p>
        )}
        {isApplications && (
          <p className="text-sm text-amber-200/80">
            Apply for staff, item creation, lead, or admin roles within Howling Hills organizations.
          </p>
        )}
      </header>

      {loading ? (
        <p className="text-sm text-amber-200/80">Loading form configuration…</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4 rounded-xl border border-amber-900/70 bg-black/30 p-4">
          {error && <p className="text-xs text-red-400 mb-1">{error}</p>}
          {success && <p className="text-xs text-emerald-300 mb-1">{success}</p>}

          {/* Identity block */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            {!isFeedback || !anonymous ? (
              <>
                {isReport ? (
                  <>
                    <TextField
                      label="Your SL Username"
                      value={slUsername}
                      onChange={setSlUsername}
                      required
                    />
                    <TextField
                      label="Your Discord Handle (optional)"
                      value={discordUsername}
                      onChange={setDiscordUsername}
                      required={false}
                    />
                  </>
                ) : (
                  <>
                    <TextField
                      label="Name"
                      value={name}
                      onChange={setName}
                      required
                    />
                    <TextField
                      label="Second Life Username"
                      value={slUsername}
                      onChange={setSlUsername}
                      required
                    />
                    <TextField
                      label="Discord Username (optional)"
                      value={discordUsername}
                      onChange={setDiscordUsername}
                      required={false}
                    />
                  </>
                )}
              </>
            ) : (
              <p className="text-xs text-amber-200/80">
                Anonymous mode enabled: we will not collect your name or usernames for this feedback.
              </p>
            )}
          </div>

          {/* Category-specific fixed controls */}
          {isReport && (
            <div className="space-y-2 text-xs">
              <label className="block space-y-1">
                <span className="text-amber-100 font-medium">
                  Staff member you are reporting<span className="text-red-400">*</span>
                </span>
                <select
                  value={reportedStaffId}
                  onChange={(e) => setReportedStaffId(e.target.value)}
                  className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                >
                  <option value="">Select staff member…</option>
                  {staff.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.username}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {isFeedback && (
            <div className="space-y-2 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                <label className="block space-y-1">
                  <span className="text-amber-100 font-medium">
                    Feedback type<span className="text-red-400">*</span>
                  </span>
                  <select
                    value={feedbackType}
                    onChange={(e) => setFeedbackType(e.target.value)}
                    className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                  >
                    <option value="">Select type…</option>
                    <option value="HUD Feedback">HUD Feedback</option>
                    <option value="General Feedback">General Feedback</option>
                    <option value="Build Feedback">Build Feedback</option>
                    <option value="Backer Feedback">Backer Feedback</option>
                  </select>
                </label>

                <label className="inline-flex items-center gap-2 text-xs text-amber-200/90 mt-2 sm:mt-6">
                  <input
                    type="checkbox"
                    checked={anonymous}
                    onChange={(e) => setAnonymous(e.target.checked)}
                    className="h-3 w-3"
                  />
                  <span>Submit anonymously</span>
                </label>
              </div>
            </div>
          )}

          {isApplications && (
            <div className="space-y-3 text-xs">
              <label className="block space-y-1">
                <span className="text-amber-100 font-medium">
                  Application type<span className="text-red-400">*</span>
                </span>
                <select
                  value={appType}
                  onChange={(e) => {
                    const nextType = e.target.value;
                    setAppType(nextType);
                    setAnswers({});
                    setAppDepartments([]);
                    setStaffPositions({});
                    setStaffAck(false);
                    setLeadOrg("");
                  }}
                  className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                >
                  <option value="">Select type…</option>
                  <option value="Staff Application">Staff Application</option>
                  <option value="Item Creation">Item Creation</option>
                  <option value="Lead Application">Lead Application</option>
                  <option value="Admin Application">Admin Application</option>
                  <option value="Sim VIP/Backers Waitlist">Sim VIP/Backers Waitlist</option>
                </select>
              </label>

              {appType === "Staff Application" && (
                <div className="space-y-2 rounded-md border border-amber-900/70 bg-black/30 p-3">
                  {settings.staff_disclaimer_text && (
                    <p className="text-[11px] leading-[1.6] text-amber-200/90 whitespace-pre-line">
                      {settings.staff_disclaimer_text}
                    </p>
                  )}
                  <label className="inline-flex items-center gap-2 text-[11px] text-amber-200/90">
                    <input
                      type="checkbox"
                      checked={staffAck}
                      onChange={(e) => setStaffAck(e.target.checked)}
                      className="h-3 w-3"
                    />
                    <span>I have read and acknowledge the staff disclaimer.</span>
                  </label>

                  {settings.perks_text && (
                    <div className="mt-2 border-t border-amber-900/60 pt-2">
                      <h3 className="font-semibold text-amber-100 text-xs mb-1">Perks</h3>
                      <p className="text-[11px] leading-[1.6] text-amber-200/90 whitespace-pre-line">
                        {settings.perks_text}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {appType === "Lead Application" && (
                <div className="space-y-2 rounded-md border border-amber-900/70 bg-black/30 p-3">
                  {settings.lead_disclaimer_text && (
                    <p className="text-[11px] leading-[1.6] text-amber-200/90 whitespace-pre-line">
                      {settings.lead_disclaimer_text}
                    </p>
                  )}
                  <label className="block space-y-1 mt-2">
                    <span className="text-amber-100 font-medium text-xs">Organization<span className="text-red-400">*</span></span>
                    <select
                      data-testid="lead-organization-select"
                      value={leadOrg}
                      onChange={(e) => setLeadOrg(e.target.value)}
                      className="w-full bg-[#17110e] border border-[#4b3624] text-amber-100 rounded px-2 py-1 text-xs"
                    >
                      <option value="">Select organization…</option>
                      {organizationPositions.map((org) => {
                        const isClosed = org.open === false;
                        return (
                          <option key={org.id} value={org.id} disabled={isClosed}>
                            {isClosed ? `[Closed] ${org.name}` : org.name}
                          </option>
                        );
                      })}
                    </select>
                  </label>
                </div>
              )}

              {appType === "Admin Application" && (
                <div className="space-y-2 rounded-md border border-amber-900/70 bg-black/30 p-3">
                  {settings.admin_disclaimer_text && (
                    <p className="text-[11px] leading-[1.6] text-amber-200/90 whitespace-pre-line">
                      {settings.admin_disclaimer_text}
                    </p>
                  )}
                </div>
              )}

              {appType === "Staff Application" && (
                <div className="space-y-1 text-xs">
                  <span className="text-amber-100 font-medium text-xs">Staff Departments</span>
                  <div className="flex flex-wrap gap-2">
                    {staffDepartments.map((d) => {
                      const isClosed = d.open === false;
                      const label = `${isClosed ? "[Closed] " : ""}${d.name}`;
                      const checked = appDepartments.includes(d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          disabled={isClosed}
                          onClick={() => {
                            const set = new Set(appDepartments);
                            if (set.has(d.id)) set.delete(d.id);
                            else set.add(d.id);
                            setAppDepartments(Array.from(set));
                          }}
                          className={`px-2 py-0.5 rounded-full border text-[11px] ${
                            checked
                              ? "bg-amber-600 text-black border-amber-400"
                              : isClosed
                              ? "bg-black/40 text-amber-500 border-amber-900/80 cursor-not-allowed"
                              : "bg-black/40 text-amber-100 border-amber-700/60 hover:bg-amber-900/40"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  {appDepartments.length > 0 && (
                    <div className="mt-3 space-y-2 text-xs">
                      <span className="text-amber-100 font-medium text-xs">Positions for selected departments</span>
                      <div className="space-y-2">
                        {staffDepartments
                          .filter((d) => appDepartments.includes(d.id))
                          .map((d) => {
                            const deptPositions = (d.positions || []).filter((p) => p.open !== false);
                            if (!deptPositions.length) return null;
                            const selectedForDept = staffPositions[d.id] || [];
                            const toggle = (posId) => {
                              setStaffPositions((prev) => {
                                const current = prev[d.id] || [];
                                const set = new Set(current);
                                if (set.has(posId)) set.delete(posId);
                                else set.add(posId);
                                return { ...prev, [d.id]: Array.from(set) };
                              });
                            };
                            return (
                              <div key={d.id} className="space-y-1">
                                <p className="text-[11px] text-amber-200/90">
                                  {d.name} positions
                                </p>
                                <div className="flex flex-wrap gap-2">
                                  {deptPositions.map((p) => (
                                    <button
                                      key={p.id}
                                      type="button"
                                      onClick={() => toggle(p.id)}
                                      className={`px-2 py-0.5 rounded-full border text-[11px] ${
                                        selectedForDept.includes(p.id)
                                          ? "bg-amber-500 text-black border-amber-300"
                                          : "bg-black/40 text-amber-100 border-amber-700/60 hover:bg-amber-900/40"
                                      }`}
                                    >
                                      {p.name}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {appType === "Admin Application" && (
                <div className="space-y-1 text-xs">
                  <span className="text-amber-100 font-medium text-xs">Admin Departments</span>
                  <div className="flex flex-wrap gap-2">
                    {adminDepartments.map((d) => {
                      const isClosed = d.open === false;
                      const label = `${isClosed ? "[Closed] " : ""}${d.name}`;
                      const checked = appDepartments.includes(d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          disabled={isClosed}
                          onClick={() => {
                            const set = new Set(appDepartments);
                            if (set.has(d.id)) set.delete(d.id);
                            else set.add(d.id);
                            setAppDepartments(Array.from(set));
                          }}
                          className={`px-2 py-0.5 rounded-full border text-[11px] ${
                            checked
                              ? "bg-amber-600 text-black border-amber-400"
                              : isClosed
                              ? "bg-black/40 text-amber-500 border-amber-900/80 cursor-not-allowed"
                              : "bg-black/40 text-amber-100 border-amber-700/60 hover:bg-amber-900/40"
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Dynamic questions */}
          <QuestionRenderer
            questions={currentQuestions}
            answers={answers}
            setAnswers={setAnswers}
          />

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-black px-4 py-1.5 text-xs font-semibold"
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
